import { useState } from "react";
import { useTranslation } from "@privacy/translations";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  UpdateDpiaConnectedTenantErrorCodes,
  myTenantsHierarchyWithCreatePermissionQueryOptions,
  updateDpiaConnectedTenantMutationOptions,
} from "../../api/dpia";
import type { DpiaById } from "../../api/dpia.types";
import { hasErrorCode } from "../../api/errors";
import { DynamicForm } from "../../dynamicForm";
import { ErrorMessage } from "../../dynamicForm/inlineForm/ErrorMessage";
import { TenantSelect } from "../../shared/TenantSelect";
import type { TenantHierarchy } from "../../api/users.types";
import { usePermission } from "../../shared/permissions";
import { TenantIcon3 } from "../../shared/icons";
import { DpiaConnectedTenantWarningDialog } from "./DpiaConnectedTenantWarningDialog";

export const CONNECTED_TENANT_INPUT_ID = "dpia-connected-tenant";

type DpiaConnectedTenantProps = { dpia: DpiaById; isReadOnly: boolean };

export const DpiaConnectedTenantSelect = ({ dpia, isReadOnly }: DpiaConnectedTenantProps) => {
  const { t } = useTranslation();
  const { inputState, setIsEditing } = DynamicForm.useInlineInputState(isReadOnly);
  const { hasPermission } = usePermission();
  const [wasHovered, setWasHovered] = useState(false);

  const updateConnectedTenantMutation = useMutation(updateDpiaConnectedTenantMutationOptions());
  const tenantHierarchyQuery = useQuery({
    ...myTenantsHierarchyWithCreatePermissionQueryOptions(),
    enabled: inputState !== "readonly" && (inputState === "editing" || wasHovered),
  });

  const isTenantDisabled = (tenant: TenantHierarchy) =>
    !hasPermission("nova-forms-api:dpia:create", tenant.permissions);

  const responsibleHasNoAccessToNewTenant = hasErrorCode(
    updateConnectedTenantMutation.error,
    UpdateDpiaConnectedTenantErrorCodes.responsibleHasNoAccessToNewTenant
  );
  const isMoveInProgress = updateConnectedTenantMutation.isPending || responsibleHasNoAccessToNewTenant;
  const tenant = (isMoveInProgress && updateConnectedTenantMutation.variables) || dpia.connectedTenant;

  return (
    <Stack sx={{ width: "100%" }}>
      <TenantSelect
        id={CONNECTED_TENANT_INPUT_ID}
        blurOnSelect
        disabled={isReadOnly || updateConnectedTenantMutation.isPending}
        selectedId={tenant.tenantId}
        selectedNameWhileLoading={tenant.tenantName}
        tenantHierarchy={tenantHierarchyQuery.data}
        InputProps={{
          startAdornment: <TenantIcon3 size={16} />,
          sx: DynamicForm.inlineInputStateStyles[inputState],
        }}
        onBlur={() => setIsEditing(false)}
        onFocus={() => setIsEditing(true)}
        onMouseOver={() => setWasHovered(true)}
        onSelect={({ organizationName: tenantName, id: tenantId }) =>
          updateConnectedTenantMutation.mutate({ dpiaId: dpia.id, tenantId, tenantName })
        }
        forceDisabledTenant={isTenantDisabled}
        renderOption={(optionElement, option) => (
          <Tooltip
            key={option.id}
            title={isTenantDisabled(option) && t("CreateDpia.Dialog.MissingPermissionsOnTenantTooltip")}
            followCursor
          >
            {optionElement}
          </Tooltip>
        )}
      />
      {updateConnectedTenantMutation.isError && !responsibleHasNoAccessToNewTenant && <ErrorMessage />}
      {responsibleHasNoAccessToNewTenant && (
        <DpiaConnectedTenantWarningDialog
          dpia={{ id: dpia.id, name: dpia.name }}
          tenant={{ id: tenant.tenantId, name: tenant.tenantName }}
          onClose={() => updateConnectedTenantMutation.reset()}
        />
      )}
    </Stack>
  );
};

export const DpiaConnectedTenant = ({ dpia, isReadOnly }: DpiaConnectedTenantProps) => {
  const { t } = useTranslation();

  return (
    <>
      <Typography variant="h6" component="label" htmlFor={CONNECTED_TENANT_INPUT_ID}>
        {t("DpiaDrawer.ConnectedTenant.Label")}
      </Typography>
      <Box sx={{ marginLeft: "-12px", display: "flex", alignItems: "center" }}>
        <DpiaConnectedTenantSelect dpia={dpia} isReadOnly={isReadOnly} />
      </Box>
    </>
  );
};
